import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Grid, Divider, Chip, Tooltip, Table, TableBody, TableCell, TableHead, TableRow, Collapse, IconButton, LinearProgress } from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import DnsIcon from '@mui/icons-material/Dns';
import ComputerIcon from '@mui/icons-material/Computer';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Helper for value display
const InfoItem = ({ label, value, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    {icon && <Box sx={{ mr: 1 }}>{icon}</Box>}
    <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 90 }}>{label}:</Typography>
    <Typography variant="body2" sx={{ ml: 1 }}>{value ?? '-'}</Typography>
  </Box>
);

// CPU Panel
function CPUPanel({ cpuStaticInfo, cpuUsagePercent }) {
  if (!cpuStaticInfo || cpuStaticInfo.length === 0) return null;
  const totalCores = cpuStaticInfo.reduce((sum, c) => sum + (c.cores || 1), 0);
  const primaryCPU = cpuStaticInfo[0];
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom><DnsIcon sx={{ mr: 1 }} />CPU</Typography>
        <InfoItem label="Model Name" value={primaryCPU?.model || '-'} />
        <InfoItem label="Total Cores" value={totalCores} />
        <Box sx={{ mt: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Usage</Typography>
            <Typography variant="body2" color="primary">{cpuUsagePercent != null ? `${cpuUsagePercent.toFixed(1)}%` : '-'}</Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={cpuUsagePercent || 0} 
            sx={{ height: 8, borderRadius: 1 }} 
          />
        </Box>
        <Divider sx={{ my: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Model</TableCell>
              <TableCell>Cores</TableCell>
              <TableCell>Speed (GHz)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cpuStaticInfo.map((cpu, idx) => (
              <TableRow key={idx}>
                <TableCell>{cpu.model}</TableCell>
                <TableCell>{cpu.cores}</TableCell>
                <TableCell>{cpu.speedGhz}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// RAM Panel
function RAMPanel({ ramInfo }) {
  if (!ramInfo) return null;
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom><MemoryIcon sx={{ mr: 1 }} />RAM</Typography>
        <InfoItem label="Total" value={ramInfo.totalGb ? `${ramInfo.totalGb.toFixed(2)} GB` : '-'} />
        <InfoItem label="Used" value={ramInfo.usedGb ? `${ramInfo.usedGb.toFixed(2)} GB` : '-'} />
        <Box sx={{ mt: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Usage</Typography>
            <Typography variant="body2" color="primary">
              {ramInfo.usedPercent != null ? `${ramInfo.usedPercent.toFixed(1)}%` : '-'}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={ramInfo.usedPercent || 0} 
            sx={{ height: 8, borderRadius: 1 }} 
          />
        </Box>
      </CardContent>
    </Card>
  );
}

// Collapsible Panel Component
function CollapsiblePanel({ title, icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        {icon}
        <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>{title}</Typography>
        <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Divider />
        <CardContent>{children}</CardContent>
      </Collapse>
    </Card>
  );
}

// Disk Panel
function DiskPanel({ diskInfo }) {
  if (!diskInfo || !Array.isArray(diskInfo) || diskInfo.length === 0) return null;
  return (
    <CollapsiblePanel title="Disks" icon={<StorageIcon sx={{ mr: 1 }} />} defaultOpen={false}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Device</TableCell>
            <TableCell>Mount</TableCell>
            <TableCell>FS</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Used</TableCell>
            <TableCell>Free</TableCell>
            <TableCell>Usage</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {diskInfo.map((disk, idx) => (
            <TableRow key={idx}>
              <TableCell>{disk.device}</TableCell>
              <TableCell>{disk.mountpoint}</TableCell>
              <TableCell>{disk.fstype}</TableCell>
              <TableCell>{disk.totalGb ? `${disk.totalGb.toFixed(2)} GB` : '-'}</TableCell>
              <TableCell>{disk.usedGb ? `${disk.usedGb.toFixed(2)} GB` : '-'}</TableCell>
              <TableCell>{disk.freeGb ? `${disk.freeGb.toFixed(2)} GB` : '-'}</TableCell>
              <TableCell>
                <Box sx={{ minWidth: 120 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{disk.usedPercent != null ? `${disk.usedPercent.toFixed(1)}%` : '-'}</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={disk.usedPercent || 0} 
                    sx={{ height: 6, borderRadius: 1 }} 
                  />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CollapsiblePanel>
  );
}

// GPU Panel
function GPUPanel({ gpuInfo }) {
  if (!gpuInfo || !Array.isArray(gpuInfo) || gpuInfo.length === 0) return null;
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom><DeveloperBoardIcon sx={{ mr: 1 }} />GPU</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vendor</TableCell>
              <TableCell>Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gpuInfo.map((gpu, idx) => (
              <TableRow key={idx}>
                <TableCell>{gpu.vendor}</TableCell>
                <TableCell>{gpu.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// PCI Panel
function PCIPanel({ pciInfo }) {
  if (!pciInfo || !Array.isArray(pciInfo) || pciInfo.length === 0) return null;
  return (
    <CollapsiblePanel title="PCI Devices" icon={<InfoIcon sx={{ mr: 1 }} />} defaultOpen={false}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Class</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pciInfo.map((pci, idx) => (
            <TableRow key={idx}>
              <TableCell>{pci.vendor}</TableCell>
              <TableCell>{pci.name}</TableCell>
              <TableCell>{pci.class}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CollapsiblePanel>
  );
}

// OS and Hardware Panel
function OSAndHardwarePanel({ osInfo, uptimeSeconds, bootTimeUnix, cpuStaticInfo, cpuUsagePercent, ramInfo, gpuInfo, pciInfo }) {
  // OS info
  let uptime = '-';
  if (uptimeSeconds) {
    const days = Math.floor(uptimeSeconds / 86400);
    const hours = Math.floor((uptimeSeconds % 86400) / 3600);
    const mins = Math.floor((uptimeSeconds % 3600) / 60);
    uptime = `${days}d ${hours}h ${mins}m`;
  }
  let bootTime = '-';
  if (bootTimeUnix) {
    const date = new Date(bootTimeUnix * 1000);
    bootTime = date.toLocaleString();
  }
  // CPU summary
  let cpuModel = '-';
  let totalCores = 0;
  if (cpuStaticInfo && cpuStaticInfo.length > 0) {
    cpuModel = cpuStaticInfo[0].model || '-';
    totalCores = cpuStaticInfo.reduce((sum, c) => sum + (c.cores || 1), 0);
  }
  return (
    <>
      <Card variant="outlined" sx={{ mb: 2, width: '100%' }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Box display="flex" flexDirection="row" alignItems="stretch" width="100%" sx={{ m: 0 }}>
            <Box flex={1} minWidth={0} sx={{ px: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><ComputerIcon sx={{ mr: 1 }} />Operating System</Typography>
              <InfoItem label="Platform" value={osInfo?.platform} />
              <InfoItem label="Version" value={osInfo?.version} />
              <InfoItem label="Kernel" value={osInfo?.kernel} />
              <InfoItem label="Arch" value={osInfo?.arch} />
              <InfoItem label="Uptime" value={uptime} />
              <InfoItem label="Boot Time" value={bootTime} />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
            <Box flex={1} minWidth={0} sx={{ px: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><DnsIcon sx={{ mr: 1 }} />CPU</Typography>
              <InfoItem label="Model Name" value={cpuModel} />
              <InfoItem label="Total Cores" value={totalCores || '-'} />
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Usage:</Typography>
                  <Typography variant="body2" color="primary">
                    {cpuUsagePercent != null ? `${cpuUsagePercent.toFixed(1)}%` : '-'}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={cpuUsagePercent || 0} 
                  sx={{ height: 8, borderRadius: 1 }} 
                />
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
            <Box flex={1} minWidth={0} sx={{ px: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><MemoryIcon sx={{ mr: 1 }} />RAM</Typography>
              <InfoItem label="Total" value={ramInfo?.totalGb ? `${ramInfo.totalGb.toFixed(2)} GB` : '-'} />
              <InfoItem label="Used" value={ramInfo?.usedGb ? `${ramInfo.usedGb.toFixed(2)} GB` : '-'} />
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Usage:</Typography>
                  <Typography variant="body2" color="primary">
                    {ramInfo?.usedPercent != null ? `${ramInfo.usedPercent.toFixed(1)}%` : '-'}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={ramInfo?.usedPercent || 0} 
                  sx={{ height: 8, borderRadius: 1 }} 
                />
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
            <Box flex={1} minWidth={0} sx={{ px: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><DeveloperBoardIcon sx={{ mr: 1 }} />GPU / AI Accelerator</Typography>
              {/* GPU List */}
              {gpuInfo && gpuInfo.length > 0 ? (
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {gpuInfo.map((g, idx) => (
                    <li key={"gpu-"+idx} style={{ listStyle: 'disc', marginLeft: 16 }}>
                      <Typography variant="body2">{g.vendor ? `${g.vendor} ${g.name}` : g.name}</Typography>
                    </li>
                  ))}
                  {/* Hailo PCI Devices */}
                  {(() => {
                    const hailoDevices = pciInfo && pciInfo.length > 0 ? pciInfo.filter(pci => (pci.vendor && pci.vendor.toLowerCase().includes('hailo')) || (pci.name && pci.name.toLowerCase().includes('hailo'))) : [];
                    if (hailoDevices.length > 0) {
                      let label = 'Single';
                      if (hailoDevices.length === 2) label = 'Dual';
                      else if (hailoDevices.length === 3) label = 'Triple';
                      else if (hailoDevices.length >= 4) label = 'Quad';
                      return (
                        <li key="hailo-ai-accel" style={{ listStyle: 'disc', marginLeft: 16 }}>
                          <Typography variant="body2" color="primary">
                            AI Accelerator: {hailoDevices[0].vendor} {hailoDevices[0].name} ({label})
                          </Typography>
                        </li>
                      );
                    }
                    return null;
                  })()}
                </Box>
              ) : (
                <>
                  {/* Show Hailo even if no GPU */}
                  {(() => {
                    const hailoDevices = pciInfo && pciInfo.length > 0 ? pciInfo.filter(pci => (pci.vendor && pci.vendor.toLowerCase().includes('hailo')) || (pci.name && pci.name.toLowerCase().includes('hailo'))) : [];
                    if (hailoDevices.length > 0) {
                      let label = 'Single';
                      if (hailoDevices.length === 2) label = 'Dual';
                      else if (hailoDevices.length === 3) label = 'Triple';
                      else if (hailoDevices.length >= 4) label = 'Quad';
                      return (
                        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                          <li key="hailoonly-ai-accel" style={{ listStyle: 'disc', marginLeft: 16 }}>
                            <Typography variant="body2" color="primary">
                              AI Accelerator: {hailoDevices[0].vendor} {hailoDevices[0].name} ({label})
                            </Typography>
                          </li>
                        </Box>
                      );
                    }
                    return <InfoItem label="Name" value="-" />;
                  })()}
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}

// Main SystemInfoPanel
export default function SystemInfoPanel({ systemInfo }) {
  console.log("SystemInfoPanel received systemInfo:", systemInfo); // Debug log
  console.log("CPU Usage:", systemInfo?.cpuUsagePercent);
  console.log("RAM Info:", systemInfo?.ramInfo);
  if (!systemInfo) return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="body1" color="text.secondary"><InfoIcon sx={{ mr: 1 }} />No system info available.</Typography>
      </CardContent>
    </Card>
  );
  return (
    <Box>
      <OSAndHardwarePanel
        osInfo={systemInfo.osInfo}
        uptimeSeconds={systemInfo.uptimeSeconds}
        bootTimeUnix={systemInfo.bootTimeUnix}
        cpuStaticInfo={systemInfo.cpuStaticInfo}
        cpuUsagePercent={systemInfo.cpuUsagePercent}
        ramInfo={systemInfo.ramInfo}
        gpuInfo={systemInfo.gpuInfo}
        pciInfo={systemInfo.pciInfo}
      />
      <DiskPanel diskInfo={systemInfo.diskInfo} />
      <PCIPanel pciInfo={systemInfo.pciInfo} />
    </Box>
  );
}
